import React from 'react';
import { WIDGET_ICON_MAP } from '../../../widgetIconMap';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const timeToMinutes = (s) => {
    if (!s || s === '--:--') return -1;
    const [h, m] = s.split(':').map(Number);
    return h * 60 + m;
};

const StateScreen = ({ msg, isError }) => (
    <div style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#fafaf9', direction: 'rtl',
    }}>
        <span style={{ fontSize: '1.125rem', color: isError ? '#c0392b' : '#94a3b8' }}>{msg}</span>
    </div>
);

const Centered = ({
    moadData, processedCustomTimes, loading, error,
    title = 'מועד', showTitle = true, days = [],
    entryIcon = 'Sun', exitIcon = 'Star', dayLabels = [],
    drashotByDay = {},
    showLeyning, showHaftarah, leyningDisplayMode,
}) => {
    const EntryIcon = WIDGET_ICON_MAP[entryIcon] || WIDGET_ICON_MAP['Sun'];
    const ExitIcon  = WIDGET_ICON_MAP[exitIcon]  || WIDGET_ICON_MAP['Star'];

    if (loading) return <StateScreen msg="טוען זמני מועד..." />;
    if (error)   return <StateScreen msg={error} isError />;
    if (!moadData?.days?.length) return <StateScreen msg="אין נתוני מועד" />;

    const firstDay = moadData.days[0];
    const lastDay  = moadData.days[moadData.days.length - 1];
    const entryTime = firstDay?.entryTime || '--:--';
    const exitTime  = lastDay?.exitTime   || '--:--';
    const entryLabel = dayLabels[0]?.entry || "זמן א'";
    const exitLabel  = dayLabels[moadData.days.length - 1]?.exit || "זמן ב'";
    const EffectiveEntryIcon = WIDGET_ICON_MAP[dayLabels[0]?.entryIcon] || EntryIcon;
    const EffectiveExitIcon  = WIDGET_ICON_MAP[dayLabels[moadData.days.length - 1]?.exitIcon] || ExitIcon;
    const showEntry = dayLabels[0]?.entry !== null;
    const showExit  = dayLabels[moadData.days.length - 1]?.exit !== null;

    const timesByDay = {};
    (processedCustomTimes || []).forEach(ct => {
        const idx = ct.dayIndex ?? 0;
        if (!timesByDay[idx]) timesByDay[idx] = [];
        timesByDay[idx].push(ct);
    });
    Object.values(timesByDay).forEach(g =>
        g.sort((a, b) => (a._sortKey ?? timeToMinutes(a.time)) - (b._sortKey ?? timeToMinutes(b.time)))
    );

    const hasCustomTimes = (processedCustomTimes || []).length > 0;

    return (
        <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            direction: 'rtl', boxSizing: 'border-box',
            backgroundColor: '#fafaf9',
            padding: '24px 32px',
            overflow: 'hidden',
        }}>
            {/* Title */}
            {showTitle && (
                <div style={{
                    fontSize: '1.25rem', fontWeight: 500,
                    color: '#94a3b8', letterSpacing: '0.12em',
                    textTransform: 'uppercase', marginBottom: 20,
                    textAlign: 'center',
                }}>
                    {title}
                </div>
            )}

            {/* Entry */}
            {showEntry && (
                <div style={{
                    display: 'flex', alignItems: 'center',
                    gap: 16, marginBottom: 8,
                }}>
                    <EffectiveEntryIcon style={{ width: 22, height: 22, color: '#166534', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#64748b', minWidth: 80, textAlign: 'center' }}>
                        {entryLabel}
                    </span>
                    <span style={{
                        fontSize: '3.5rem', fontWeight: 800,
                        color: '#1e3a5f', letterSpacing: '-0.02em',
                        fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                        lineHeight: 1,
                    }}>
                        {entryTime}
                    </span>
                </div>
            )}

            {/* Divider */}
            {showEntry && showExit && (
                <div style={{
                    width: '60%', height: 1,
                    backgroundColor: '#e5ddd3',
                    margin: '12px 0',
                }} />
            )}

            {/* Exit */}
            {showExit && (
                <div style={{
                    display: 'flex', alignItems: 'center',
                    gap: 16, marginBottom: hasCustomTimes ? 28 : 0,
                }}>
                    <EffectiveExitIcon style={{ width: 22, height: 22, color: '#64748b', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#64748b', minWidth: 80, textAlign: 'center' }}>
                        {exitLabel}
                    </span>
                    <span style={{
                        fontSize: '3.5rem', fontWeight: 800,
                        color: '#1e3a5f', letterSpacing: '-0.02em',
                        fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                        lineHeight: 1,
                    }}>
                        {exitTime}
                    </span>
                </div>
            )}

            {/* דרשות */}
            {moadData.days.some((_, i) => drashotByDay[i]) && (
                <div style={{
                    width: '100%', maxWidth: 480,
                    textAlign: 'center', marginBottom: hasCustomTimes ? 16 : 0,
                }}>
                    <div style={{ width: '40%', height: 1, backgroundColor: '#e5ddd3', margin: '0 auto 12px' }} />
                    {moadData.days.map((day, dayIdx) => {
                        const drasha = drashotByDay[dayIdx];
                        if (!drasha) return null;
                        const dayColor = days[dayIdx]?.color || '#166534';
                        return (
                            <div key={`drasha-${dayIdx}`} style={{ marginBottom: 10 }}>
                                {moadData.days.length > 1 && (
                                    <div style={{
                                        fontSize: '0.7rem', fontWeight: 700,
                                        color: dayColor, letterSpacing: '0.08em',
                                        marginBottom: 4, opacity: 0.75,
                                    }}>
                                        {day.label}
                                    </div>
                                )}
                                <div style={{
                                    fontSize: '0.95rem', fontWeight: 500,
                                    color: '#1e3a5f', fontStyle: 'italic',
                                    whiteSpace: 'pre-wrap', lineHeight: 1.4,
                                }}>
                                    {drasha}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Leyning */}
            {(showLeyning || showHaftarah) && moadData.days.some(d => d.leyning) && (
                <div style={{
                    width: '100%', maxWidth: 480,
                    textAlign: 'center', marginBottom: hasCustomTimes ? 16 : 0,
                }}>
                    <div style={{ width: '40%', height: 1, backgroundColor: '#e5ddd3', margin: '0 auto 12px' }} />
                    {moadData.days.map((day, dayIdx) => {
                        if (!day.leyning) return null;
                        const dayColor = days[dayIdx]?.color || '#166534';
                        return (
                            <div key={`leyning-${dayIdx}`} style={{ marginBottom: 10 }}>
                                {moadData.days.length > 1 && (
                                    <div style={{
                                        fontSize: '0.7rem', fontWeight: 700,
                                        color: dayColor, letterSpacing: '0.08em',
                                        marginBottom: 4, opacity: 0.75,
                                    }}>
                                        {day.label}
                                    </div>
                                )}
                                <LeyningDisplay
                                    leyning={day.leyning}
                                    mode={leyningDisplayMode}
                                    showHaftarah={showHaftarah}
                                    showMaftir={leyningDisplayMode === 'full'}
                                    compact={true}
                                    style={{ padding: '8px 0' }}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Custom times — compact pills centered */}
            {hasCustomTimes && (
                <div style={{
                    width: '100%', maxWidth: 480,
                    display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                    <div style={{ width: '40%', height: 1, backgroundColor: '#e5ddd3', alignSelf: 'center', marginBottom: 4 }} />
                    {moadData.days.map((day, dayIdx) => {
                        const dayTimes = timesByDay[dayIdx];
                        if (!dayTimes?.length) return null;
                        const dayColor = days[dayIdx]?.color || '#166534';
                        return (
                            <div key={dayIdx}>
                                {moadData.days.length > 1 && (
                                    <div style={{
                                        fontSize: '0.7rem', fontWeight: 700,
                                        color: dayColor, letterSpacing: '0.08em',
                                        textAlign: 'center', marginBottom: 6,
                                        opacity: 0.75,
                                    }}>
                                        {day.label}
                                    </div>
                                )}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                                    {dayTimes.map((ct, idx) => (
                                        <div key={idx} style={{
                                            display: 'flex', alignItems: 'center', gap: 6,
                                            padding: '4px 14px',
                                            borderRadius: 99,
                                            backgroundColor: '#fff',
                                            border: '1px solid #e5ddd3',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                        }}>
                                            <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>
                                                {ct.label}
                                            </span>
                                            <span style={{
                                                fontSize: '0.95rem', fontWeight: 700,
                                                color: '#1e3a5f',
                                                fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                                            }}>
                                                {ct.time}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Centered;
